import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo } from 'react';
import { Path, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConsentStatusDto, useProductCenterApi } from 'api';
import { applicableConsentsQuery } from 'api/queries';
import { ControlledRadioButtonGroup } from 'library/components/controls/form/ControlledRadioButtonGroup';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import {
	EndCustomerWithLanguageDto,
	MARKETING_CONSENT_LEGAL_REFERENCE,
	STORAGE_CONSENT_LEGAL_REFERENCE,
} from 'modules/EndCustomer/Shared/types';

import { GdprDialog } from './GdprDialog';
import { mapUserLanguageToConsentLanguage } from './Mappers';

const FIELD_PREFIX = 'consentApprovals';
const getFieldPrefix = (index: number) =>
	`${FIELD_PREFIX}.${index}.value` as Path<EndCustomerWithLanguageDto>;

type NewCustomerConsentsProps = {
	disabled?: string[]; // by legalReference
};

export const NewCustomerConsents = ({ disabled }: NewCustomerConsentsProps) => {
	const { t } = useTranslation(['commonButton']);
	const { language } = useAppSelector(selectUser);

	const { control, setValue, watch } =
		useFormContext<EndCustomerWithLanguageDto>();
	const watchConsentApprovals = watch('consentApprovals');
	const isStorageConsentGiven = watchConsentApprovals?.find(
		({ legalReference }) =>
			legalReference === STORAGE_CONSENT_LEGAL_REFERENCE
	)?.value;
	const { fields, append, replace } = useFieldArray({
		control,
		name: FIELD_PREFIX,
	});
	const { api } = useProductCenterApi();
	const { data } = useQuery(
		applicableConsentsQuery(api, mapUserLanguageToConsentLanguage(language))
	);

	const consentOptions = useMemo(
		() => [
			{ id: true, label: t('agree') },
			{ id: false, label: t('disagree') },
		],
		[t]
	);

	// a temporary fix to register dynamic fields in react-hook-form
	// ideally the form schema should be known before rendering the form
	// this is a workaround to register fields when they are loaded from the server
	useEffect(() => {
		if (!data) return;

		// Build desired array exactly matching data
		const next = data
			.sort((a, b) => a.legalReference.localeCompare(b.legalReference))
			.map(({ legalReference, title }) => ({
				value: null,
				legalReference,
				consentName: title,
			})) as Omit<ConsentStatusDto, 'value'>[];

		// Only update if lengths or content differ
		if (fields.length !== next.length) {
			replace(next as ConsentStatusDto[]);
		}
	}, [data, replace, append, fields]);

	// if storage consent is not given, marketing consent must be disabled and set to false automatically, as marketing consent is dependent on storage consent
	useEffect(() => {
		if (isStorageConsentGiven === false) {
			const index = fields.findIndex(
				({ legalReference }) =>
					legalReference === MARKETING_CONSENT_LEGAL_REFERENCE
			);
			if (index !== -1) {
				setValue(getFieldPrefix(index), false);
			}
		}
	}, [isStorageConsentGiven]);

	return (
		<>
			{data &&
				fields.map(({ id }, index) => (
					<Fragment key={id}>
						<Box
							sx={{
								position: 'relative',
							}}>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'nowrap',
									alignItems: 'center',
									gap: 2,
								}}>
								<Typography variant="subtitle2">
									{data[index].title}
									<Typography
										component="span"
										color="error"
										variant="subtitle2">
										{' *'}
									</Typography>
								</Typography>
								{data[index].content && (
									<GdprDialog content={data[index].content} />
								)}
							</Box>
							{data[index].warning && (
								<Typography
									variant="body2"
									sx={{
										ml: 8,
									}}>
									<Warning
										color="warning"
										sx={{ verticalAlign: 'bottom', mr: 2 }}
									/>
									{data[index].warning}
								</Typography>
							)}
						</Box>

						<ControlledRadioButtonGroup
							disabled={disabled?.includes(
								data[index].legalReference
							)}
							options={consentOptions}
							row
							key={id}
							name={getFieldPrefix(index)}
							control={control}
							required={isStorageConsentGiven !== false}
							radioProps={{ color: 'secondary' }}
						/>
					</Fragment>
				))}
		</>
	);
};
